import { createFileRoute, redirect } from '@tanstack/react-router'
import { InlineError } from '@/components/ui/inline-error'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { AdminNav } from '@/components/admin/admin-nav'
import { fetchCapabilities } from '@/hooks/use-capabilities'
import { useTenantModules, useToggleModule } from '@/hooks/use-tenant-modules'
import { MODULE_REGISTRY } from '@/lib/modules'
import { supabase } from '@/lib/supabase'

export const Route = createFileRoute('/_authed/admin/modules')({
  beforeLoad: async () => {
    // settings.manage is the CANONICAL discriminator for module management —
    // never a role-name guess (ADR-0002: UI gating is presentation; RLS on
    // tenant_modules is the authorization boundary and refuses the write
    // regardless). R29: the parent's aggregate guard alone walked
    // audit.view-only users into six live toggles the database refuses.
    const {
      data: { user },
    } = await supabase.auth.getUser()
    if (!user) throw redirect({ to: '/login' })

    const capabilities = await fetchCapabilities(user.id)
    // A parent-admitted user without settings.manage holds users.manage, so
    // /admin forwards them to the tab they CAN open — never a bounce loop.
    if (!capabilities.canManageSettings) throw redirect({ to: '/admin' })
  },
  component: ModulesPage,
})

function ModulesPage() {
  // isPending, NOT isLoading — v5's `isLoading` is `isPending && isFetching`,
  // so a pending-but-paused query (offline, cold cache) reports not-loading
  // with no data. And `isError` must be handled at all, because
  // `isModuleEnabled(undefined, key)` returns TRUE: default-on lives in the
  // resolver, not in the data. An unresolved read therefore rendered every
  // switch ENABLED — a false statement about tenant-level access control — and
  // `onCheckedChange` derives `next` from that wrong `checked`, so a single
  // click wrote a change the admin never intended.
  // `use-module-availability.ts` documents this exact fail-open and closes it;
  // this page had reopened it.
  const { isModuleEnabled, isPending, isError, refetch } = useTenantModules()
  const toggle = useToggleModule()

  const intro = (
    <p className="mb-4 text-sm text-muted-foreground">
      Modules are on by default. Turning one off hides its navigation entry and blocks its pages
      for everyone in this workspace. Accounts and contacts are core and cannot be disabled.
    </p>
  )

  if (isPending) {
    return (
      <div className="p-6">
        <AdminNav />
        <div className="max-w-2xl">
          {intro}
          <div className="space-y-2">
            {MODULE_REGISTRY.map((m) => (
              <Skeleton key={m.key} className="h-16 w-full" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (isError) {
    // Fail CLOSED: state the failure and render NO switches. Showing them
    // defaulted-on would be both a false claim and a live mis-write surface.
    return (
      <div className="p-6">
        <AdminNav />
        <div className="max-w-2xl">
          {intro}
          <InlineError what="module settings" onRetry={() => void refetch()} />
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Slice 1B-1: "Admin" is titled by the ONE shared header. */}
      <AdminNav />
      <div className="max-w-2xl">
        {intro}
        <div className="divide-y border rounded-lg overflow-hidden">
            {MODULE_REGISTRY.map((module) => {
              const enabled = isModuleEnabled(module.key)
              return (
                <div key={module.key} className="flex items-center justify-between px-4 py-3">
                  <div className="pr-4">
                    <p className="text-sm font-medium">{module.label}</p>
                    <p className="text-xs text-muted-foreground">{module.description}</p>
                  </div>
                  <Switch
                    checked={enabled}
                    disabled={toggle.isPending}
                    onCheckedChange={(next) => toggle.mutate({ key: module.key, enabled: next })}
                    aria-label={`${enabled ? 'Disable' : 'Enable'} ${module.label}`}
                  />
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}
